// MARK://dhk8/admin-shell/lib/clients/media
// Image/file uploads → dhk8-media (Akamai Object Storage, S3-compatible, us-lax-4).
// Used initially for product images (CatalogPage). The service exposes:
//   POST   /v1/media/upload   multipart "file" → 201 { ok, data: { url, key, contentType, size } }
//   DELETE /v1/media          body { key }     → 200 { ok, data: { key } }
//
// NOTE: we can't reuse `api.ts`'s `apiFetch`/`apiData` here because that wrapper
// always sets `Content-Type: application/json`, which breaks multipart (the
// browser must set the boundary). So this client does its own `fetch`, but reuses
// the same headers convention (`x-dhk8-tenant-id` / `x-dhk8-user-id` / bearer).
//
// TODO(wire-up): call `uploadImage(file)` from the product image picker in
// `src/pages/CatalogPage.tsx` — on file select, upload, then store the returned
// `url` (and `key`, for later deletion via `deleteImage(key)`) on the product
// record sent to dhk8-catalog-menus. Not wired here to avoid churning the
// catalog editor; the contract above is stable.
import { apiBase } from '../api-origin.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authSession } from '../../stores/auth-store.js'
import { prepareImageForUpload } from '../image-compress.js'

export interface UploadedMedia {
  url: string
  key: string
  contentType: string
  size: number
}

const MEDIA_SVC = 'dhk8-media'

function mediaUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${apiBase()}/${MEDIA_SVC}${p}`
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const sess = authSession()
  return {
    ...(sess?.tenantId ? { 'x-dhk8-tenant-id': sess.tenantId } : {}),
    ...(sess?.userId ? { 'x-dhk8-user-id': sess.userId } : {}),
    ...(sess?.token ? { Authorization: `Bearer ${sess.token}` } : {}),
    ...extra,
  }
}

/** Upload an image file. Returns its public URL + storage key. */
export async function uploadImage(file: File): Promise<UploadedMedia> {
  if (PREVIEW_STUB) {
    return mock<UploadedMedia>({
      url: `https://cdn.example/preview/${encodeURIComponent(file.name)}`,
      key: `tenants/preview/products/${file.name}`,
      contentType: file.type || 'image/png',
      size: file.size,
    })
  }
  // Normaliza a JPEG ≤ límite + reescala ANTES de subir: el media service solo
  // acepta png/jpeg/webp/gif ≤ 5 MB, así que esto elimina los fallos por HEIC
  // (iPhone) y por archivos grandes. Best-effort: si falla, sube el original.
  const prepared = await prepareImageForUpload(file)
  const fd = new FormData()
  fd.append('file', prepared, prepared.name)
  // NB: do NOT set Content-Type — the browser adds the multipart boundary.
  const res = await fetch(mediaUrl('/v1/media/upload'), {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
    credentials: 'include',
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const data = await res.json()
      msg = data?.error?.message ?? data?.message ?? msg
    } catch {
      /* ignore */
    }
    throw new Error(`media upload failed: ${msg}`)
  }
  const json = (await res.json()) as { ok: boolean; data?: UploadedMedia; error?: { message?: string } }
  if (!json.ok || !json.data) throw new Error(`media upload failed: ${json.error?.message ?? 'bad response'}`)
  return json.data
}

/** Delete a previously uploaded object by its storage key (tenant-scoped server-side). */
export async function deleteImage(key: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await fetch(mediaUrl('/v1/media'), {
    method: 'DELETE',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ key }),
    credentials: 'include',
  })
  if (!res.ok && res.status !== 404) {
    throw new Error(`media delete failed: HTTP ${res.status}`)
  }
}
